import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PhoneButton from '@/components/base/PhoneButton';
import { getMahalleBySlug } from '@/mocks/bolge-data';

const services = [
  { icon: 'ri-door-open-line', title: 'Acil Kapı Açma' },
  { icon: 'ri-lock-2-line', title: 'Kilit Değiştirme' },
  { icon: 'ri-shield-keyhole-line', title: 'Çelik Kapı Açma' },
  { icon: 'ri-car-line', title: 'Oto Çilingir' },
  { icon: 'ri-safe-line', title: 'Kasa Açma' },
  { icon: 'ri-key-2-line', title: 'Anahtar Kopyalama' },
];

export default function MahalleLandingPage() {
  const { bolgeSlug, mahalleSlug } = useParams<{ bolgeSlug: string; mahalleSlug: string }>();
  const result = getMahalleBySlug(bolgeSlug ?? '', mahalleSlug ?? '');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    if (result) {
      const { bolge, mahalle } = result;
      const title = `${mahalle.name} ${bolge.name} Çilingir | Alfa Çilingir 7/24 - 0541 624 79 82`;
      const desc = `${mahalle.fullName}, ${bolge.name} çilingir hizmeti. Acil kapı açma, kilit değiştirme. 7/24 hizmet, 15 dakikada yanınızda. 0541 624 79 82`;

      document.title = title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', desc);

      // Canonical
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
      canonical.href = `${siteUrl}/${bolge.slug}/${mahalle.slug}`;

      // Schema.org JSON-LD
      const existingScript = document.getElementById('schema-mahalle');
      if (existingScript) existingScript.remove();

      const faqItems = [
        {
          q: `${mahalle.name} Mahallesi'nde çilingir kaç dakikada gelir?`,
          a: `${mahalle.fullName}, ${bolge.name} bölgesinde çağrı aldıktan sonra ortalama 15 dakika içinde adresinizde oluyoruz. 7/24 hizmet veriyoruz.`,
        },
        {
          q: `${mahalle.name}'de gece çilingir hizmeti var mı?`,
          a: `Evet! ${mahalle.fullName} dahil ${bolge.name}'nin tüm mahallelerinde gece gündüz, hafta sonu ve tatil günleri dahil 7/24 hizmet veriyoruz.`,
        },
        {
          q: `${mahalle.name} Mahallesi çilingir fiyatı ne kadar?`,
          a: `Fiyatlarımız hizmet türüne göre değişmektedir. İşlem öncesinde net fiyat bilgisi veriyoruz, gizli ücret yoktur. Bilgi için: 0541 624 79 82`,
        },
        {
          q: `${mahalle.name}'de çelik kapı açılıyor mu?`,
          a: `Evet, ${mahalle.fullName}'nde tüm marka çelik kapılarda hasarsız açma hizmeti veriyoruz. Uzman ekibimiz özel ekipmanlarla kapınıza zarar vermeden açar.`,
        },
      ];

      const schema = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'LocalBusiness',
            '@id': `${siteUrl}/${bolge.slug}/${mahalle.slug}#localbusiness`,
            name: 'Alfa Çilingir',
            url: `${siteUrl}/${bolge.slug}/${mahalle.slug}`,
            telephone: '+905416247982',
            description: desc,
            address: {
              '@type': 'PostalAddress',
              streetAddress: mahalle.fullName,
              addressLocality: bolge.name,
              addressRegion: 'İstanbul',
              addressCountry: 'TR',
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                opens: '00:00',
                closes: '23:59',
              },
            ],
            areaServed: {
              '@type': 'Place',
              name: `${mahalle.fullName}, ${bolge.name}`,
            },
            priceRange: '₺₺',
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
              { '@type': 'ListItem', position: 2, name: 'Hizmet Bölgeleri', item: `${siteUrl}/hizmet-bolgeleri` },
              { '@type': 'ListItem', position: 3, name: bolge.title, item: `${siteUrl}/${bolge.slug}` },
              { '@type': 'ListItem', position: 4, name: `${mahalle.name} Çilingir`, item: `${siteUrl}/${bolge.slug}/${mahalle.slug}` },
            ],
          },
        ],
      };

      const script = document.createElement('script');
      script.id = 'schema-mahalle';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);

      // Separate FAQPage schema — avoids Google "repeated" warning
      const existingFaqScript = document.getElementById('schema-mahalle-faq');
      if (existingFaqScript) existingFaqScript.remove();

      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      };

      const faqScript = document.createElement('script');
      faqScript.id = 'schema-mahalle-faq';
      faqScript.type = 'application/ld+json';
      faqScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(faqScript);
    }

    return () => {
      const s = document.getElementById('schema-mahalle');
      if (s) s.remove();
      const f = document.getElementById('schema-mahalle-faq');
      if (f) f.remove();
      const c = document.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    };
  }, [result]);

  if (!result) return <Navigate to="/hizmet-bolgeleri" replace />;

  const { bolge, mahalle } = result;

  const faqs = [
    {
      q: `${mahalle.name} Mahallesi'nde çilingir kaç dakikada gelir?`,
      a: `${mahalle.fullName}, ${bolge.name} bölgesinde çağrı aldıktan sonra ortalama 15 dakika içinde adresinizde oluyoruz. 7/24 hizmet veriyoruz.`,
    },
    {
      q: `${mahalle.name}'de gece çilingir hizmeti var mı?`,
      a: `Evet! ${mahalle.fullName} dahil ${bolge.name}'nin tüm mahallelerinde gece gündüz, hafta sonu ve tatil günleri dahil 7/24 hizmet veriyoruz.`,
    },
    {
      q: `${mahalle.name} Mahallesi çilingir fiyatı ne kadar?`,
      a: `Fiyatlarımız hizmet türüne göre değişmektedir. İşlem öncesinde net fiyat bilgisi veriyoruz, gizli ücret yoktur. Bilgi için: 0541 624 79 82`,
    },
    {
      q: `${mahalle.name}'de çelik kapı açılıyor mu?`,
      a: `Evet, ${mahalle.fullName}'nde tüm marka çelik kapılarda hasarsız açma hizmeti veriyoruz. Uzman ekibimiz özel ekipmanlarla kapınıza zarar vermeden açar.`,
    },
  ];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={bolge.img}
              alt={`${mahalle.name} ${bolge.name} Çilingir`}
              className="w-full h-full object-cover object-top opacity-35"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-white/60 mb-5 flex-wrap">
              <Link to="/" className="hover:text-white cursor-pointer">Ana Sayfa</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              <Link to="/hizmet-bolgeleri" className="hover:text-white cursor-pointer">Hizmet Bölgeleri</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              <Link to={`/${bolge.slug}`} className="hover:text-white cursor-pointer">{bolge.name}</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              <span className="text-white">{mahalle.name}</span>
            </nav>

            <div className="inline-flex items-center gap-2 border border-orange-400/60 text-orange-300 text-xs font-bold px-4 py-2 rounded-full mb-5 backdrop-blur-sm bg-white/5">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-ping absolute"></span>
              <span className="w-2 h-2 bg-orange-400 rounded-full relative"></span>
              7/24 ACİL ÇİLİNGİR
            </div>

            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              {mahalle.name} <span className="text-orange-400">Çilingir</span>
            </h1>
            <p className="text-gray-200 text-lg mb-2">
              <strong className="text-white">{mahalle.fullName}</strong>, {bolge.name}
            </p>
            <p className="text-gray-300 text-base mb-8 max-w-xl">
              7/24 profesyonel çilingir hizmeti. Acil kapı açma, kilit değiştirme ve daha fazlası. <strong className="text-white">15 dakikada yanınızda.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Click-to-Call Ana Buton */}
              <a
                href="tel:05416247982"
                className="inline-flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-400 text-white font-extrabold px-8 py-4 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-lg shadow-lg shadow-orange-500/30 animate-pulse"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-fill text-xl"></i></span>
                Şimdi Ara — 0541 624 79 82
              </a>
              <Link
                to={`/${bolge.slug}`}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/60 text-white hover:bg-white/10 font-semibold px-6 py-4 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-sm"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                {bolge.name} Sayfası
              </Link>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 md:py-20 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Left */}
              <div className="lg:w-3/5">
                <h2 className="text-2xl font-extrabold text-gray-900 mb-4">
                  {mahalle.fullName} Çilingir Hizmeti
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  Alfa Çilingir olarak <strong>{mahalle.fullName}</strong>, {bolge.name} bölgesinde 7/24 profesyonel çilingir hizmeti sunuyoruz. Kapınız kilitli kaldıysa, anahtarınızı kaybettiyseniz veya kilit değiştirmek istiyorsanız hemen arayın.
                </p>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  {mahalle.name} Mahallesi'nde acil kapı açma, kilit değiştirme, çelik kapı açma, oto çilingir ve kasa açma hizmetleri için uzman ekibimiz her an hazır. Çağrı aldıktan sonra ortalama <strong>15 dakika</strong> içinde adresinizde oluyoruz.
                </p>

                {/* Services */}
                <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider mb-4">Verdiğimiz Hizmetler</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                  {services.map((svc) => (
                    <div key={svc.title} className="bg-white rounded-xl p-3 border border-stone-100 flex items-center gap-2">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-50 text-orange-500 rounded-lg flex-shrink-0">
                        <i className={`${svc.icon} text-base`}></i>
                      </span>
                      <span className="text-gray-700 text-xs font-medium">{svc.title}</span>
                    </div>
                  ))}
                </div>

                {/* Why Us */}
                <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider mb-4">Neden Alfa Çilingir?</h3>
                <div className="space-y-2">
                  {[
                    `${mahalle.name} Mahallesi'nde 15 dakikada yanınızda`,
                    'Güvenilir ve deneyimli çilingirler',
                    'Hasarsız kapı açma garantisi',
                    'İşlem öncesi şeffaf fiyat, gizli ücret yok',
                    '7/24 gece gündüz, tatil dahil hizmet',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-5 h-5 flex items-center justify-center text-orange-500 flex-shrink-0">
                        <i className="ri-check-double-line"></i>
                      </span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: CTA Card */}
              <div className="lg:w-2/5">
                <div className="bg-white rounded-2xl border border-stone-100 p-6 sticky top-24">
                  <div className="text-center mb-5">
                    <span className="w-16 h-16 flex items-center justify-center bg-orange-50 text-orange-500 rounded-2xl mx-auto mb-3">
                      <i className="ri-phone-fill text-3xl"></i>
                    </span>
                    <h3 className="font-extrabold text-gray-900 text-lg">Hemen Arayın</h3>
                    <p className="text-gray-400 text-sm mt-1">{mahalle.name} Mahallesi için 7/24 hizmet</p>
                  </div>
                  <a
                    href="tel:05416247982"
                    className="flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-extrabold py-4 rounded-xl whitespace-nowrap cursor-pointer transition-all duration-200 text-lg w-full mb-3"
                  >
                    <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
                    Şimdi Ara — 0541 624 79 82
                  </a>
                  <a
                    href="https://wa.me/905416247982"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl whitespace-nowrap cursor-pointer transition-all duration-200 text-sm w-full mb-4"
                  >
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-base"></i></span>
                    WhatsApp ile Yazış
                  </a>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3 text-gray-600">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-50 text-orange-500 rounded-lg flex-shrink-0">
                        <i className="ri-timer-flash-line"></i>
                      </span>
                      <span>Ortalama <strong>15 dakika</strong> içinde yanınızda</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-50 text-orange-500 rounded-lg flex-shrink-0">
                        <i className="ri-24-hours-line"></i>
                      </span>
                      <span><strong>7/24</strong> kesintisiz hizmet</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-50 text-orange-500 rounded-lg flex-shrink-0">
                        <i className="ri-shield-check-line"></i>
                      </span>
                      <span>Güvenilir ve deneyimli çilingirler</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-50 text-orange-500 rounded-lg flex-shrink-0">
                        <i className="ri-price-tag-3-line"></i>
                      </span>
                      <span>Şeffaf fiyat, gizli ücret yok</span>
                    </div>
                  </div>

                  <div className="border-t border-stone-100 mt-5 pt-4">
                    <p className="text-xs text-gray-400 text-center">
                      <span className="w-4 h-4 inline-flex items-center justify-center text-orange-400 mr-1">
                        <i className="ri-map-pin-line"></i>
                      </span>
                      {mahalle.fullName}, {bolge.name}, İstanbul
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 text-orange-500 text-xs font-bold uppercase tracking-widest mb-3">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-question-line"></i></span>
                SSS
              </span>
              <h2 className="text-2xl font-extrabold text-gray-900">
                {mahalle.name} Çilingir Hakkında Sık Sorulan Sorular
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-stone-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                    <span className={`w-6 h-6 flex items-center justify-center text-orange-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-xl"></i>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-gray-500 text-sm leading-relaxed border-t border-stone-50">
                      <p className="pt-3">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Other Neighborhoods */}
        <section className="py-12 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-lg font-extrabold text-gray-900 mb-5">
              {bolge.name}'deki Diğer Mahalleler
            </h2>
            <div className="flex flex-wrap gap-2">
              {bolge.mahalleler
                .filter((m) => m.slug !== mahalleSlug)
                .map((m) => (
                  <Link
                    key={m.slug}
                    to={`/${bolge.slug}/${m.slug}`}
                    className="bg-white border border-stone-200 hover:border-orange-300 hover:text-orange-600 text-gray-600 text-xs px-3 py-2 rounded-full transition-all cursor-pointer"
                  >
                    {m.name} Çilingir
                  </Link>
                ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-orange-500">
          <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
              {mahalle.name} Mahallesi'nde Acil Çilingir mi Lazım?
            </h2>
            <p className="text-orange-100 mb-6">Hemen arayın, 15 dakikada yanınızda olalım.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:05416247982"
                className="inline-flex items-center gap-3 bg-white text-orange-500 font-extrabold px-10 py-5 rounded-full whitespace-nowrap cursor-pointer hover:bg-orange-50 transition-all duration-200 text-xl"
              >
                <span className="w-7 h-7 flex items-center justify-center"><i className="ri-phone-fill text-2xl"></i></span>
                Şimdi Ara — 0541 624 79 82
              </a>
              <a
                href="https://wa.me/905416247982"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-extrabold px-8 py-5 rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 text-base"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-whatsapp-line text-xl"></i></span>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
